import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiPlusBold,
    PiPencilSimpleBold,
    PiTrashBold,
    PiFloppyDiskBold,
    PiXBold,
    PiTrophyBold,
    PiWarningBold,
    PiCheckCircleBold,
} from 'react-icons/pi';
import { Button, PageHeader, LoadingState } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { ownerAvatarColor, ownerInitials } from '@v2/features/ploomes-deals/utils';
import type { CommissionGoal, CommissionGoalPerformance } from './types';

// ─── Formatters ──────────────────────────────────────────────────────────────

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
}

/** Máscara BRL para inputs — inteiro, sem centavos. */
function formatBrlInput(value: number): string {
    if (!value) return '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
}

function parseBrlInput(raw: string): number {
    const digits = raw.replace(/\D/g, '');
    return digits ? Number(digits) : 0;
}

function pct(v: number) {
    return `${(v * 100).toFixed(1)}%`;
}

const BUCKET_COLORS: Record<string, string> = {
    force: 'bg-blue-500',
    cardio: 'bg-red-500',
    gluteleader: 'bg-purple-500',
    accessories: 'bg-green-500',
};

const BUCKET_LABELS: Record<string, string> = {
    force: 'Força',
    cardio: 'Cardio',
    gluteleader: 'Gluteleader',
    accessories: 'Acessórios',
};

const DEFAULT_WEIGHTS = { force: 0.45, cardio: 0.25, gluteleader: 0.15, accessories: 0.15 };

// ─── Period picker ────────────────────────────────────────────────────────────

function periodOptions() {
    const opts = [];
    const now = new Date();
    for (let i = -2; i <= 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        opts.push({ val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
}

// ─── SpiffBadge ───────────────────────────────────────────────────────────────

function SpiffBadge({ perf }: { perf: CommissionGoalPerformance }) {
    if (perf.is_exempt_spiff) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                Isento de Spiff
            </span>
        );
    }
    if (!perf.reached_100) {
        const p = (perf.pct_global * 100).toFixed(1);
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {p}% da meta
            </span>
        );
    }
    if (perf.earns_bonus) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                <PiTrophyBold className="w-3 h-3" /> Dobra salário
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
            <PiWarningBold className="w-3 h-3" /> Só Força+Cardio
        </span>
    );
}

const BUCKET_STROKE_COLORS: Record<string, string> = {
    force: '#3b82f6',       // blue-500
    cardio: '#ef4444',      // red-500
    gluteleader: '#a855f7', // purple-500
    accessories: '#22c55e', // green-500
};

const BUCKET_RULES: Record<string, string> = {
    force: 'REALLEADER (exceto Gluteleader e esteira/escada)',
    cardio: 'SPIRIT · REALLEADER esteiras e escadas',
    gluteleader: 'REALLEADER GLUTELEADER',
    accessories: 'OKPRO · NEWFIELD',
};

function GoalSellerAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
    if (avatarUrl) {
        return (
            <img
                src={avatarUrl}
                alt={name}
                className="h-full min-h-[9rem] w-full object-cover"
            />
        );
    }

    return (
        <div
            className="flex h-full min-h-[9rem] w-full items-center justify-center text-2xl font-black text-white"
            style={{ backgroundColor: ownerAvatarColor(name) }}
        >
            {ownerInitials(name)}
        </div>
    );
}

function analysisHref(userId: number, period: string, bucket?: string) {
    const params = new URLSearchParams({ period });
    if (bucket) params.set('bucket', bucket);
    return `/commissioning/goals/${userId}/analysis?${params.toString()}`;
}

// ─── BucketDonut ─────────────────────────────────────────────────────────────

function BucketDonut({
    bucket,
    label,
    pctRaw,
    achieved,
    href,
}: {
    bucket: string;
    label: string;
    pctRaw: number;
    achieved: number;
    href: string;
}) {
    const size = 56;
    const stroke = 6;
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const filled = Math.min(1, pctRaw);
    const dash = filled * circ;
    const color = BUCKET_STROKE_COLORS[bucket] ?? '#94a3b8';
    const pctDisplay = (pctRaw * 100).toFixed(0);

    return (
        <Link
            to={href}
            title={`${BUCKET_RULES[bucket]} — ver deals e produtos`}
            className="group flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg p-0.5 transition hover:bg-white/80"
        >
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
                    <circle
                        cx={size / 2} cy={size / 2} r={r}
                        fill="none"
                        stroke={color}
                        strokeWidth={stroke}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xs font-bold leading-none text-gray-800">{pctDisplay}%</span>
                </div>
            </div>
            <span className="max-w-full truncate text-[10px] font-medium text-center leading-tight text-orange-700 group-hover:underline">
                {label}
            </span>
            <span className="text-[10px] tabular-nums text-gray-400">{fmt(achieved)}</span>
        </Link>
    );
}

// ─── BucketDonuts ────────────────────────────────────────────────────────────

function BucketDonuts({
    buckets,
    userId,
    period,
}: {
    buckets: CommissionGoalPerformance['buckets'];
    userId: number;
    period: string;
}) {
    return (
        <div className="mt-2.5 flex w-full gap-1">
            {buckets.map((b) => (
                <BucketDonut
                    key={b.bucket}
                    bucket={b.bucket}
                    label={b.label}
                    pctRaw={b.pct}
                    achieved={b.achieved}
                    href={analysisHref(userId, period, b.bucket)}
                />
            ))}
        </div>
    );
}

type UserOption = { id: number; name: string; email: string; roles?: string[] };

function GoalForm({
    initial,
    users,
    period,
    onSave,
    onCancel,
    loading,
}: {
    initial: Partial<CommissionGoal>;
    users: UserOption[];
    period: string;
    onSave: (data: Partial<CommissionGoal>) => void;
    onCancel: () => void;
    loading: boolean;
}) {
    const [sellerQuery, setSellerQuery] = useState('');
    const [form, setForm] = useState({
        user_id: initial.user_id ?? '',
        period: period || initial.period,
        global_target: initial.global_target ?? 0,
        fixed_salary: initial.fixed_salary ?? 0,
        bucket_weights: initial.bucket_weights ?? { ...DEFAULT_WEIGHTS },
        is_exempt_spiff: initial.is_exempt_spiff ?? false,
        notes: initial.notes ?? '',
    });
    const sellerNeedle = sellerQuery.trim().toLowerCase();
    const visibleUsers = sellerNeedle === ''
        ? users
        : users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(sellerNeedle));
    const set = (field: string, value: unknown) => setForm((p) => ({ ...p, [field]: value }));
    const setWeight = (bucket: string, v: string) =>
        setForm((p) => ({ ...p, bucket_weights: { ...p.bucket_weights, [bucket]: parseFloat(v) || 0 } }));

    return (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!initial.id && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Vendedor</label>
                        <input
                            className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                            value={sellerQuery}
                            onChange={(e) => setSellerQuery(e.target.value)}
                            placeholder="Buscar por nome ou e-mail"
                        />
                        <select
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                            value={form.user_id}
                            onChange={(e) => set('user_id', parseInt(e.target.value))}
                        >
                            <option value="">Selecione… ({visibleUsers.length})</option>
                            {visibleUsers.map((u) => (
                                <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
                            ))}
                        </select>
                        <p className="mt-1 text-[11px] text-gray-500">
                            Só aparece quem já é usuário do Hub. Pessoa nova precisa ser cadastrada em Usuários antes.
                        </p>
                    </div>
                )}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Meta Global (R$)</label>
                    <input type="text" inputMode="numeric"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={formatBrlInput(form.global_target)}
                        onChange={(e) => set('global_target', parseBrlInput(e.target.value))}
                        placeholder="R$ 0"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Salário Fixo (R$)</label>
                    <input type="text" inputMode="numeric"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={formatBrlInput(form.fixed_salary)}
                        onChange={(e) => set('fixed_salary', parseBrlInput(e.target.value))}
                        placeholder="R$ 0"
                    />
                </div>
            </div>

            <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Pesos por Bucket (padrão Q3: 45/25/15/15)</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(BUCKET_LABELS).map(([key, label]) => (
                        <div key={key}>
                            <label className="block text-xs text-gray-500 mb-1">{label} (%)</label>
                            <input type="number" min="0" max="100" step="1"
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                                value={((form.bucket_weights as Record<string, number>)[key] ?? 0) * 100}
                                onChange={(e) => setWeight(key, String(parseFloat(e.target.value || '0') / 100))}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="rounded"
                    checked={form.is_exempt_spiff}
                    onChange={(e) => set('is_exempt_spiff', e.target.checked)}
                />
                Isento do Super Gatilho
            </label>

            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
                <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    placeholder="Ex: Meta ajustada para Q3"
                />
            </div>

            <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={onCancel} className="flex items-center gap-1">
                    <PiXBold className="w-4 h-4" /> Cancelar
                </Button>
                <Button onClick={() => onSave(form)} disabled={loading} className="flex items-center gap-1">
                    <PiFloppyDiskBold className="w-4 h-4" />
                    {loading ? 'Salvando…' : 'Salvar'}
                </Button>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CommissionGoalsPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const periods = periodOptions();
    const [period, setPeriod] = useState(periods.find((p) => p.val === new Date().toISOString().slice(0, 7))?.val ?? periods[2].val);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [deletingGoal, setDeletingGoal] = useState<CommissionGoal | null>(null);

    const { data, isLoading } = useQuery<{ data: CommissionGoal[] }>({
        queryKey: ['commissioning', 'goals', period],
        queryFn: () => api.get('/commissioning/goals', { params: { period } }).then((r) => r.data),
    });

    const { data: usersData } = useQuery<{ data: UserOption[] }>({
        queryKey: ['commissioning', 'goals', 'seller-options'],
        queryFn: () => api.get('/commissioning/goals/seller-options').then((r) => r.data),
    });

    const goals = data?.data ?? [];
    const users: UserOption[] = usersData?.data ?? [];

    const createMutation = useMutation({
        mutationFn: (d: Partial<CommissionGoal>) => api.post('/commissioning/goals', d),
        onSuccess: () => { toast.success('Meta criada!'); qc.invalidateQueries({ queryKey: ['commissioning', 'goals'] }); setAdding(false); },
        onError: () => toast.error('Erro ao criar.'),
    });

    const updateMutation = useMutation({
        mutationFn: (d: Partial<CommissionGoal>) => api.put(`/commissioning/goals/${d.id}`, d),
        onSuccess: () => { toast.success('Meta atualizada!'); qc.invalidateQueries({ queryKey: ['commissioning', 'goals'] }); setEditingId(null); },
        onError: () => toast.error('Erro ao atualizar.'),
    });

    const deleteMutation = useMutation({
        mutationFn: ({ id, period }: { id: number; period: string }) => api.delete(`/commissioning/goals/${id}`, { params: { period } }),
        onSuccess: () => {
            toast.success('Removido!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'goals'] });
            setDeletingGoal(null);
        },
        onError: () => toast.error('Erro ao remover.'),
    });

    return (
        <div className="space-y-6">
            <ConfirmModal
                open={deletingGoal !== null}
                onClose={() => !deleteMutation.isPending && setDeletingGoal(null)}
                onConfirm={() => deletingGoal && deleteMutation.mutate({ id: deletingGoal.id, period: deletingGoal.period })}
                title="Remover meta"
                description={
                    deletingGoal
                        ? t('v2.commissioning.goals_remove_description', { name: deletingGoal.user_name, period: deletingGoal.period })
                        : 'Remover esta meta?'
                }
                confirmLabel="Sim, remover"
                cancelLabel="Cancelar"
                loading={deleteMutation.isPending}
            />

            <PageHeader
                title="Metas por Bucket"
                subtitle="Super Gatilho Q3 — salário dobrado com diversificação de portfólio"
            >
                <div className="flex items-center gap-3">
                    <select
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                    >
                        {periods.map((p) => <option key={p.val} value={p.val}>{p.label}</option>)}
                    </select>
                    <Button onClick={() => setAdding(true)} className="flex items-center gap-2">
                        <PiPlusBold className="w-4 h-4" /> Nova Meta
                    </Button>
                </div>
            </PageHeader>

            {/* Regra de ouro info */}
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-2 text-sm text-amber-800">
                <PiWarningBold className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                    <strong>Regra de Ouro:</strong> Atingir 100% da meta{' '}
                    <strong>exclusivamente com Força + Cardio NÃO dobra</strong> o salário.
                    Se Força + Cardio sozinhos já cobrem a meta, Gluteleader/Acessórios extras não salvam —
                    é preciso que Gluteleader ou Acessórios entrem para <em>chegar</em> aos 100%.
                    <span className="mt-1 block text-xs text-amber-700/90">
                        Buckets: Força = REALLEADER · Cardio = SPIRIT + REALLEADER (esteiras/escadas) ·
                        Gluteleader = REALLEADER GLUTELEADER · Acessórios = OKPRO e NEWFIELD.
                    </span>
                </span>
            </div>

            <p className="text-xs text-gray-500">{t('v2.commissioning.goals_recurring_hint')}</p>

            {adding && (
                <GoalForm
                    initial={{ period }}
                    users={users}
                    period={period}
                    onSave={(d) => createMutation.mutate(d)}
                    onCancel={() => setAdding(false)}
                    loading={createMutation.isPending}
                />
            )}

            {isLoading && <LoadingState variant="table" />}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {goals.map((goal) => {
                    const perf = goal.performance;
                    return (
                        <div key={goal.id} className="min-w-0">
                            {editingId === goal.id ? (
                                <GoalForm
                                    initial={goal}
                                    users={users}
                                    period={period}
                                    onSave={(d) => updateMutation.mutate({ ...d, id: goal.id })}
                                    onCancel={() => setEditingId(null)}
                                    loading={updateMutation.isPending}
                                />
                            ) : (
                                <div className="h-full overflow-hidden rounded-xl border border-gray-200 bg-white">
                                    <div className="flex h-full min-h-[9rem]">
                                        <div className="w-[5.5rem] shrink-0 self-stretch sm:w-28">
                                            <GoalSellerAvatar
                                                name={goal.user_name}
                                                avatarUrl={goal.user_avatar_url}
                                            />
                                        </div>

                                        <div className="relative flex min-w-0 flex-1 flex-col p-3">
                                            <div className="absolute right-2 top-2 flex gap-0.5">
                                                <button onClick={() => setEditingId(goal.id)} className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                                                    <PiPencilSimpleBold className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => setDeletingGoal(goal)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <PiTrashBold className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="min-w-0 pr-14">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <Link
                                                        to={analysisHref(goal.user_id, goal.period)}
                                                        className="truncate font-semibold leading-tight text-gray-900 hover:text-orange-700 hover:underline"
                                                    >
                                                        {goal.user_name}
                                                    </Link>
                                                    {perf && <SpiffBadge perf={perf} />}
                                                    {perf?.earns_bonus && (
                                                        <span className="text-xs font-medium text-emerald-700">
                                                            +{fmt(perf.bonus_amount)} bônus
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-0.5 text-xs text-gray-500">
                                                    Meta: <strong>{fmt(goal.global_target)}</strong>
                                                    {' · '}Fixo: <strong>{fmt(goal.fixed_salary)}</strong>
                                                    {goal.is_exempt_spiff && <span className="ml-2 text-gray-400">(isento)</span>}
                                                    {goal.is_inherited && goal.source_period && (
                                                        <span className="ml-2 text-gray-400">
                                                            {t('v2.commissioning.goals_inherited_since', { period: goal.source_period })}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>

                                            {perf && (
                                                <BucketDonuts
                                                    buckets={perf.buckets}
                                                    userId={goal.user_id}
                                                    period={goal.period}
                                                />
                                            )}
                                            <Link
                                                to={analysisHref(goal.user_id, goal.period)}
                                                className="mt-auto pt-2 inline-flex text-xs font-semibold text-orange-700 hover:underline"
                                            >
                                                Ver deals e produtos por bucket →
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {goals.length === 0 && !isLoading && !adding && (
                    <div className="col-span-full rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                        Nenhuma meta cadastrada para {period}. Clique em "Nova Meta" para começar.
                    </div>
                )}
            </div>

            {/* Resumo Spiff */}
            {goals.length > 0 && (
                <SpiffSummaryCard goals={goals} />
            )}
        </div>
    );
}

function SpiffSummaryCard({ goals }: { goals: CommissionGoal[] }) {
    const earn = goals.filter((g) => g.performance?.earns_bonus);
    const total = earn.reduce((s, g) => s + (g.performance?.bonus_amount ?? 0), 0);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Resumo do Super Gatilho</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                    <p className="text-2xl font-bold text-emerald-700">{earn.length}</p>
                    <p className="text-xs text-gray-500">Dobram salário</p>
                </div>
                <div>
                    <p className="text-2xl font-bold text-red-600">{goals.filter((g) => g.performance?.reached_100 && !g.performance.earns_bonus && !g.performance.is_exempt_spiff).length}</p>
                    <p className="text-xs text-gray-500">100% mas só Força+Cardio</p>
                </div>
                <div>
                    <p className="text-2xl font-bold text-gray-800">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(total)}
                    </p>
                    <p className="text-xs text-gray-500">Total de bônus</p>
                </div>
            </div>
        </div>
    );
}
